import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLocations } from '../../store/slices/locationsSlice';
import client from '../../api/client';
import { dedupeLocations } from '../../utils/locations';
import FilterBar from '../../components/FilterBar';

// ─── shared helpers ──────────────────────────────────────────────────────────

const CATEGORY_COLOR = {
  'VC Bundle':    { bg: 'bg-brand-50',   text: 'text-brand-600',   dot: 'bg-brand-400'   },
  'Display':      { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400'   },
  'AV Equipment': { bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-400'  },
  'Cabling':      { bg: 'bg-gray-100',   text: 'text-gray-600',    dot: 'bg-gray-400'    },
  'Cloud VC':     { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  'Networking':   { bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-400'     },
  'Equipment':    { bg: 'bg-gray-100',   text: 'text-gray-600',    dot: 'bg-gray-400'    },
};

const STATUS_CONFIG = {
  Active:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  Replaced: { bg: 'bg-gray-100',   text: 'text-gray-600',    dot: 'bg-gray-400' },
  Faulty:   { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500' },
};

function fmt(n) {
  return n === 0 ? '—' : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function CategoryBadge({ category }) {
  const c = CATEGORY_COLOR[category] || CATEGORY_COLOR.Equipment;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {category}
    </span>
  );
}

const toHHMM = t => (t || '').slice(0, 5) || '09:00';

// ─── Rental breakdown modal ───────────────────────────────────────────────────

function RentalBreakdownModal({ room, onClose, showCost = true }) {
  const bd = room.rental_breakdown;
  if (!bd) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-brand-50 flex items-start justify-between flex-shrink-0">
          <div>
            <h2 className="font-bold text-brand-500 text-lg">{room.room_name}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{room.room_id} · {room.room_type} · Quarterly Rental Breakdown</p>
          </div>
          <div className="flex items-center gap-4">
            {showCost && (
              <div className="text-right">
                <p className="text-xs text-gray-400 mb-0.5">Total Quarterly</p>
                <p className="text-2xl font-bold text-brand-500">{fmt(bd.total)}</p>
              </div>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10">
              <tr style={{ background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)' }}>
                <th className="px-5 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Item</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider whitespace-nowrap">Category</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-brand-400 uppercase tracking-wider w-14">Qty</th>
                {showCost && <th className="px-4 py-3 text-right text-xs font-bold text-brand-400 uppercase tracking-wider whitespace-nowrap">Unit Price</th>}
                {showCost && <th className="px-5 py-3 text-right text-xs font-bold text-brand-400 uppercase tracking-wider whitespace-nowrap">Line Total</th>}
              </tr>
            </thead>
            <tbody>
              {bd.line_items.map((li, i) => (
                <React.Fragment key={i}>
                  {/* Billing row */}
                  <tr className={`${li.in_bundle ? 'bg-brand-50/30' : ''} ${li.devices?.length ? '' : 'border-b border-brand-50'}`}>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-gray-800">{li.name}</p>
                      {li.description && <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{li.description}</p>}
                      {li.model_part !== '(bundle)' && <p className="text-xs text-gray-300 font-mono mt-0.5">{li.model_part}</p>}
                    </td>
                    <td className="px-4 py-3 align-top"><CategoryBadge category={li.category} /></td>
                    <td className="px-4 py-3 text-center text-gray-600 font-semibold align-top">{li.qty}</td>
                    {showCost && (
                      <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap align-top">
                        {li.unit_price === 0 ? <span className="text-gray-300 text-xs">Included</span> : fmt(li.unit_price)}
                      </td>
                    )}
                    {showCost && (
                      <td className="px-5 py-3 text-right font-bold whitespace-nowrap align-top">
                        {li.unit_price === 0 ? <span className="text-gray-300">—</span> : <span className="text-gray-800">{fmt(li.line_total)}</span>}
                      </td>
                    )}
                  </tr>
                  {/* Device sub-table row — full width */}
                  {li.devices && li.devices.length > 0 && (
                    <tr className={`border-b border-brand-50 ${li.in_bundle ? 'bg-brand-50/20' : ''}`}>
                      <td colSpan={showCost ? 5 : 3} className="px-5 pb-3 pt-0">
                        <table className="w-full text-xs rounded-lg overflow-hidden border border-brand-100">
                          <thead>
                            <tr className="bg-brand-100/60">
                              {li.in_bundle && <th className="px-4 py-2 text-left font-semibold text-brand-500 uppercase tracking-wider">Device</th>}
                              {!li.in_bundle && <th className="px-4 py-2 text-left font-semibold text-brand-500 uppercase tracking-wider w-10">#</th>}
                              <th className="px-4 py-2 text-left font-semibold text-brand-500 uppercase tracking-wider">OEM</th>
                              <th className="px-4 py-2 text-left font-semibold text-brand-500 uppercase tracking-wider">Model</th>
                              <th className="px-4 py-2 text-left font-semibold text-brand-500 uppercase tracking-wider">Serial No.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {li.devices.map((d, j) => (
                              <tr key={j} className={j % 2 === 0 ? 'bg-white' : 'bg-brand-50/40'}>
                                {li.in_bundle
                                  ? <td className="px-4 py-2 text-gray-700 font-medium">{d.name || d.model || '—'}</td>
                                  : <td className="px-4 py-2 text-gray-400 font-semibold">{j + 1}</td>
                                }
                                <td className="px-4 py-2 text-gray-500">{d.oem || '—'}</td>
                                <td className="px-4 py-2 text-gray-400 font-mono">{d.model || '—'}</td>
                                <td className="px-4 py-2 font-mono text-brand-600 font-semibold tracking-wide">{d.serial || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {showCost && (
              <tfoot>
                <tr className="bg-brand-50/60">
                  <td colSpan={4} className="px-5 py-4 font-bold text-brand-500 text-right text-sm">Total Quarterly Rental</td>
                  <td className="px-5 py-4 text-right font-bold text-brand-500 text-xl">{fmt(bd.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Room card (used in locations view) ──────────────────────────────────────

function RoomCard({ room, onSelectRoom, onRoomUpdated }) {
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const showCost = !['RE', 'TEAM_LEAD'].includes(user.role);
  const [editingHours, setEditingHours] = useState(false);
  const [startTime, setStartTime] = useState(toHHMM(room.service_start_time));
  const [endTime, setEndTime] = useState(toHHMM(room.service_end_time));
  const [saving, setSaving] = useState(false);
  const rental = room.rental_breakdown?.total || 0;

  const handleSaveHours = async () => {
    setSaving(true);
    try {
      await client.patch(`/locations/rooms/${room.id}/`, {
        service_start_time: startTime,
        service_end_time: endTime,
      });
      onRoomUpdated({ id: room.id, service_start_time: startTime, service_end_time: endTime });
      setEditingHours(false);
    } catch { alert('Failed to save service hours'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-brand-100 p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="font-semibold text-brand-700 text-sm truncate">{room.room_name}</p>
          <p className="text-xs text-gray-400">{room.room_id}</p>
        </div>
        <span className="text-xs bg-brand-50 text-brand-500 font-semibold px-2 py-0.5 rounded-full flex-shrink-0 border border-brand-100">
          {room.room_type}
        </span>
      </div>
      <div className="mb-2">
        {editingHours ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="input-field text-xs py-1 w-28" />
            <span className="text-gray-400 text-xs">to</span>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="input-field text-xs py-1 w-28" />
            <button onClick={handleSaveHours} disabled={saving} className="btn-primary text-xs px-2.5 py-1">{saving ? '…' : 'Save'}</button>
            <button onClick={() => { setEditingHours(false); setStartTime(toHHMM(room.service_start_time)); setEndTime(toHHMM(room.service_end_time)); }} className="btn-secondary text-xs px-2.5 py-1">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setEditingHours(true)} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-brand-500 transition-colors group">
            <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium">{toHHMM(room.service_start_time)} – {toHHMM(room.service_end_time)}</span>
            <span className="text-gray-300 group-hover:text-brand-300">Mon–Fri</span>
            <svg className="w-3 h-3 text-gray-300 group-hover:text-brand-400 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
      </div>
      {rental > 0 ? (
        <button onClick={() => onSelectRoom(room)} className="mt-1 w-full flex items-center justify-between bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-xl px-3 py-2 transition-colors group">
          <div className="text-left">
            <p className="text-xs text-gray-400 group-hover:text-brand-400 transition-colors">{showCost ? 'Quarterly Rental' : 'Room Devices'}</p>
            {showCost && <p className="font-bold text-brand-600 text-base">₹{rental.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>}
          </div>
          <div className="flex items-center gap-1 text-xs text-magenta-500 font-semibold group-hover:text-magenta-600 transition-colors">
            {showCost ? 'View breakdown' : 'View devices'}
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </button>
      ) : (
        showCost && <div className="mt-1 bg-gray-50 rounded-xl px-3 py-2 text-xs text-gray-400 text-center border border-gray-100">No rental rate</div>
      )}
    </div>
  );
}

// ─── Location row (expandable) ────────────────────────────────────────────────

function LocationRow({ loc, isAdmin, canEditSpoc, onSelectRoom }) {
  const [expanded, setExpanded] = useState(false);
  const [rooms, setRooms] = useState(null);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [editingSpoc, setEditingSpoc] = useState(false);
  const [spocForm, setSpocForm] = useState({ spoc_name: '', spoc_phone: '', spoc_email: '', address: '', pincode: '', gstin: '' });
  const [spocSaving, setSpocSaving] = useState(false);
  const dispatch = useDispatch();

  const handleExpand = async () => {
    if (!expanded && rooms === null) {
      setLoadingRooms(true);
      try {
        const { data } = await client.get(`/locations/${loc.id}/rooms/`);
        setRooms(data.results || data);
      } catch { setRooms([]); }
      finally { setLoadingRooms(false); }
    }
    setExpanded(e => !e);
  };

  const openSpocEdit = () => {
    setSpocForm({
      spoc_name: loc.spoc_name || '', spoc_phone: loc.spoc_phone || '', spoc_email: loc.spoc_email || '',
      address: loc.address || '', pincode: loc.pincode || '', gstin: loc.gstin || '',
    });
    setEditingSpoc(true);
  };

  const saveSpoc = async () => {
    setSpocSaving(true);
    try {
      await client.patch(`/locations/${loc.id}/`, spocForm);
      dispatch(fetchLocations());
      setEditingSpoc(false);
    } catch { alert('Failed to save contact details'); }
    finally { setSpocSaving(false); }
  };

  const totalRental = loc.quarterly_rental_total || 0;

  return (
    <>
      <tr className="hover:bg-brand-50/40 transition-colors cursor-pointer" onClick={handleExpand}>
        <td className="px-5 py-2.5">
          <div className="flex items-center gap-2">
            <svg className={`w-3.5 h-3.5 text-brand-300 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-semibold text-gray-800 text-sm">{loc.name}</span>
          </div>
        </td>
        <td className="px-4 py-2.5 text-center text-gray-500 text-sm">{loc.city || '—'}</td>
        <td className="px-4 py-2.5 text-center">
          {loc.sla_class === 'A'
            ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-100 text-brand-700">Class A</span>
            : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">Class {loc.sla_class || '—'}</span>
          }
        </td>
        <td className="px-4 py-2.5 text-center text-gray-600 text-sm font-medium">{loc.room_count ?? '—'}</td>
        <td className="px-4 py-2.5 text-center" onClick={e => e.stopPropagation()}>
          {loc.spoc_name ? (
            <div className="flex flex-col items-center gap-0.5">
              <span className="font-medium text-gray-800 text-sm">{loc.spoc_name}</span>
              {loc.spoc_phone && <a href={`tel:${loc.spoc_phone}`} className="text-xs text-brand-500 hover:underline">{loc.spoc_phone}</a>}
            </div>
          ) : (
            <span className="text-gray-400 text-xs">—</span>
          )}
        </td>
        {isAdmin && (
          <td className="px-4 py-2.5 text-center">
            {totalRental > 0
              ? <span className="font-semibold text-brand-600 text-sm">₹{totalRental.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              : <span className="text-gray-400">—</span>}
          </td>
        )}
      </tr>
      {expanded && (
        <tr>
          <td colSpan={isAdmin ? 6 : 5} className="px-0 py-0 bg-brand-50/30">
            <div className="px-8 py-4 border-t border-brand-100 space-y-4">

              {/* BHEL Contact & Address */}
              <div className="bg-white rounded-xl border border-brand-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold text-brand-400 uppercase tracking-wider">BHEL Contact & Address</h4>
                  {canEditSpoc && !editingSpoc && (
                    <button onClick={openSpocEdit} className="flex items-center gap-1.5 text-xs font-semibold text-brand-500 hover:text-brand-400 px-2.5 py-1 rounded-lg hover:bg-brand-50 transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                      Edit
                    </button>
                  )}
                </div>

                {editingSpoc ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Contact Name</label>
                        <input className="input-field text-sm" placeholder="Contact name" value={spocForm.spoc_name} onChange={e => setSpocForm(p => ({ ...p, spoc_name: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Phone</label>
                        <input className="input-field text-sm" placeholder="+91 XXXXX XXXXX" value={spocForm.spoc_phone} onChange={e => setSpocForm(p => ({ ...p, spoc_phone: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Email</label>
                        <input className="input-field text-sm" type="email" placeholder="contact@bhel.in" value={spocForm.spoc_email} onChange={e => setSpocForm(p => ({ ...p, spoc_email: e.target.value }))} />
                      </div>
                      <div className="col-span-3">
                        <label className="block text-xs text-gray-500 mb-1">Address</label>
                        <textarea className="input-field text-sm resize-none" rows={2} placeholder="Full site address" value={spocForm.address} onChange={e => setSpocForm(p => ({ ...p, address: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Pincode</label>
                        <input className="input-field text-sm" placeholder="110001" value={spocForm.pincode} onChange={e => setSpocForm(p => ({ ...p, pincode: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">GSTIN</label>
                        <input className="input-field text-sm" placeholder="07AABCE1234F1Z5" value={spocForm.gstin} onChange={e => setSpocForm(p => ({ ...p, gstin: e.target.value }))} />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={saveSpoc} disabled={spocSaving} className="px-4 py-1.5 bg-brand-500 text-white rounded-lg text-xs font-semibold hover:bg-brand-400 disabled:opacity-50">{spocSaving ? 'Saving…' : 'Save'}</button>
                      <button onClick={() => setEditingSpoc(false)} className="px-4 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
                    </div>
                  </div>
                ) : (loc.spoc_name || loc.spoc_phone || loc.spoc_email || loc.address || loc.gstin) ? (
                  <div className="space-y-3 text-sm">
                    {/* Contact row */}
                    {(loc.spoc_name || loc.spoc_phone || loc.spoc_email) && (
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                          </div>
                          <span className="font-semibold text-gray-800">{loc.spoc_name || '—'}</span>
                        </div>
                        {loc.spoc_phone && (
                          <a href={`tel:${loc.spoc_phone}`} className="flex items-center gap-1.5 text-gray-500 hover:text-brand-500 transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                            {loc.spoc_phone}
                          </a>
                        )}
                        {loc.spoc_email && (
                          <a href={`mailto:${loc.spoc_email}`} className="flex items-center gap-1.5 text-gray-500 hover:text-brand-500 transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                            {loc.spoc_email}
                          </a>
                        )}
                      </div>
                    )}
                    {/* Address row */}
                    {(loc.address || loc.pincode || loc.gstin) && (
                      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gray-50">
                        {loc.address && (
                          <div className="col-span-2">
                            <p className="text-xs text-gray-400 mb-0.5">Address</p>
                            <p className="text-gray-700">{loc.address}{loc.pincode ? ` — ${loc.pincode}` : ''}</p>
                          </div>
                        )}
                        {loc.gstin && (
                          <div>
                            <p className="text-xs text-gray-400 mb-0.5">GSTIN</p>
                            <p className="font-mono text-gray-700">{loc.gstin}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                    <span>No contact person set</span>
                    {canEditSpoc && <button onClick={openSpocEdit} className="text-brand-500 hover:text-brand-400 font-semibold ml-1">Add one</button>}
                  </div>
                )}
              </div>

              {/* Rooms */}
              {loadingRooms ? (
                <div className="flex items-center gap-2 py-2 text-gray-400 text-sm">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  Loading rooms…
                </div>
              ) : rooms && rooms.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
                  {rooms.map(room => (
                    <RoomCard
                      key={room.id}
                      room={room}
                      onSelectRoom={onSelectRoom}
                      onRoomUpdated={updated => setRooms(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r))}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-2">No rooms found for this location.</p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Locations & Rooms view ───────────────────────────────────────────────────

function LocationsView({ locationFilter, isAdmin, canEditSpoc }) {
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const showCost = !['RE', 'TEAM_LEAD'].includes(user.role);
  const { list: locations, loading } = useSelector(s => s.locations);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const allLocs = dedupeLocations(locations);
  const filtered = locationFilter
    ? allLocs.filter(l => String(l.id) === String(locationFilter))
    : allLocs;

  const totalRental = allLocs.reduce((sum, l) => sum + (l.quarterly_rental_total || 0), 0);

  return (
    <>
      {selectedRoom && <RentalBreakdownModal room={selectedRoom} onClose={() => setSelectedRoom(null)} showCost={showCost} />}
      {isAdmin && totalRental > 0 && (
        <div className="flex justify-end">
          <div className="bg-white border border-brand-100 rounded-2xl px-5 py-3 shadow-sm text-right">
            <p className="text-xs text-gray-400">Total Quarterly Rental (All Locations)</p>
            <p className="text-xl font-bold text-brand-500">₹{totalRental.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)' }}>
              <th className="px-5 py-2.5 text-left text-xs font-semibold text-brand-400 uppercase tracking-wide">Location Name</th>
              <th className="px-4 py-2.5 text-center text-xs font-semibold text-brand-400 uppercase tracking-wide">City</th>
              <th className="px-4 py-2.5 text-center text-xs font-semibold text-brand-400 uppercase tracking-wide">SLA Class</th>
              <th className="px-4 py-2.5 text-center text-xs font-semibold text-brand-400 uppercase tracking-wide">Rooms</th>
              <th className="px-4 py-2.5 text-center text-xs font-semibold text-brand-400 uppercase tracking-wide">BHEL Contact</th>
              {isAdmin && <th className="px-4 py-2.5 text-center text-xs font-semibold text-brand-400 uppercase tracking-wide">Quarterly Rental</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-50">
            {loading ? (
              <tr><td colSpan={isAdmin ? 6 : 5} className="text-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <svg className="animate-spin w-7 h-7 text-magenta-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  <p className="text-gray-400 text-sm">Loading locations…</p>
                </div>
              </td></tr>
            ) : filtered.map(loc => (
              <LocationRow key={loc.id} loc={loc} isAdmin={isAdmin} canEditSpoc={canEditSpoc} onSelectRoom={setSelectedRoom} />
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="text-center py-16">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-brand-50 rounded-2xl flex items-center justify-center text-2xl">📍</div>
                  <p className="text-brand-500 font-semibold">No locations found</p>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Devices view ─────────────────────────────────────────────────────────────

function DevicesView({ locationFilter, subLocationFilter, roomFilter, search, onSubLocationChange, onRoomChange, allDevices, loading }) {
  const devices = allDevices.filter(d => {
    if (subLocationFilter && d.room?.building_name !== subLocationFilter) return false;
    if (roomFilter && d.room?.room_name !== roomFilter) return false;
    return true;
  });

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)' }}>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider whitespace-nowrap">Serial Number</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider whitespace-nowrap">Device Name</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">OEM</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Model</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider whitespace-nowrap">Install Date</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider whitespace-nowrap">Room ID</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Room</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider whitespace-nowrap">Solution Type</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Floor</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Unit</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider whitespace-nowrap">Sub Unit</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">State</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-50">
            {loading ? (
              <tr><td colSpan={13} className="text-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <svg className="animate-spin w-7 h-7 text-magenta-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  <p className="text-gray-400 text-sm">Loading devices…</p>
                </div>
              </td></tr>
            ) : devices.map(d => {
              const scfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.Active;
              return (
                <tr key={d.id} className="hover:bg-brand-50/40 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-brand-600 font-medium whitespace-nowrap">{d.serial_number}</td>
                  <td className="px-4 py-3 text-gray-800 font-medium whitespace-nowrap">{d.device_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{d.oem || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{d.model_part || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{d.installation_date || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{d.room?.room_id || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{d.room?.room_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{d.room?.solution_type || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{d.room?.floor || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{d.room?.unit || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{d.room?.sub_unit || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{d.room?.location?.state || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${scfg.bg} ${scfg.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${scfg.dot}`} />
                      {d.status || 'Active'}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!loading && devices.length === 0 && (
              <tr><td colSpan={13} className="text-center py-16">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-brand-50 rounded-2xl flex items-center justify-center text-2xl">🖥️</div>
                  <p className="text-brand-500 font-semibold">No devices found</p>
                  <p className="text-gray-400 text-sm">Try adjusting your filters</p>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function DeviceInventory() {
  const dispatch = useDispatch();
  const { list: locations } = useSelector(s => s.locations);
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'ADMIN' || user.is_staff;
  const canEditSpoc = isAdmin || user.role === 'TEAM_LEAD';

  const [view, setView] = useState('locations'); // 'locations' | 'devices'
  const [locationFilter, setLocationFilter] = useState('');
  const [subLocationFilter, setSubLocationFilter] = useState('');
  const [roomFilter, setRoomFilter] = useState('');
  const [oemFilter, setOemFilter] = useState('');
  const [search, setSearch] = useState('');

  const [allDevices, setAllDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  useEffect(() => { dispatch(fetchLocations()); }, [dispatch]);

  // Fetch devices when relevant filters change
  useEffect(() => {
    setDevicesLoading(true);
    const params = {};
    if (locationFilter) params.location = locationFilter;
    if (oemFilter) params.oem = oemFilter;
    if (search) params.search = search;
    client.get('/inventory/', { params })
      .then(({ data }) => setAllDevices(data.results || data))
      .catch(() => setAllDevices([]))
      .finally(() => setDevicesLoading(false));
  }, [locationFilter, oemFilter, search]);

  const subLocationOptions = [...new Set(
    allDevices.filter(d => d.room?.building_name).map(d => d.room.building_name)
  )].sort();

  const roomOptions = [...new Set(
    allDevices
      .filter(d => !subLocationFilter || d.room?.building_name === subLocationFilter)
      .filter(d => d.room?.room_name)
      .map(d => d.room.room_name)
  )].sort();

  const oems = [...new Set(allDevices.map(d => d.oem).filter(Boolean))].sort();

  const hasActiveFilters = !!(search || locationFilter || subLocationFilter || roomFilter || oemFilter);

  const resetAll = () => {
    setSearch('');
    setLocationFilter('');
    setSubLocationFilter('');
    setRoomFilter('');
    setOemFilter('');
  };

  const [exportingInventory, setExportingInventory] = useState(false);
  const handleExportInventory = async () => {
    setExportingInventory(true);
    try {
      const resp = await client.get('/inventory/export/', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'device_inventory_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to export inventory.');
    } finally {
      setExportingInventory(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-brand-500">Device Inventory</h1>
          <p className="text-gray-400 text-sm mt-0.5">BHEL VC equipment and site locations</p>
        </div>

        <button onClick={handleExportInventory} disabled={exportingInventory}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-brand-600 bg-brand-50 border border-brand-200 rounded-xl hover:bg-brand-100 transition-colors disabled:opacity-50">
          {exportingInventory
            ? <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          }
          Download Excel
        </button>

        {/* View toggle */}
        <div className="flex items-center bg-brand-50 border border-brand-100 rounded-xl p-1 gap-1">
          <button
            onClick={() => setView('locations')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              view === 'locations'
                ? 'bg-white text-brand-600 shadow-sm border border-brand-100'
                : 'text-gray-400 hover:text-brand-500'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Locations & Rooms
          </button>
          <button
            onClick={() => setView('devices')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              view === 'devices'
                ? 'bg-white text-brand-600 shadow-sm border border-brand-100'
                : 'text-gray-400 hover:text-brand-500'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Devices
          </button>
        </div>
      </div>

      {/* Shared filter bar */}
      <FilterBar hasActiveFilters={hasActiveFilters} onResetAll={resetAll}>
        <FilterBar.Item value={search} onClear={() => setSearch('')}>
          <div className="relative flex-1 min-w-48">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder={view === 'devices' ? 'Search serial, name…' : 'Search location…'}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-field pl-9"
            />
          </div>
        </FilterBar.Item>
        <FilterBar.Item value={locationFilter} onClear={() => { setLocationFilter(''); setSubLocationFilter(''); setRoomFilter(''); }}>
          <select
            value={locationFilter}
            onChange={e => { setLocationFilter(e.target.value); setSubLocationFilter(''); setRoomFilter(''); }}
            className="input-field w-auto"
          >
            <option value="">All Locations</option>
            {dedupeLocations(locations).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </FilterBar.Item>
        {view === 'devices' && subLocationOptions.length > 0 && (
          <FilterBar.Item value={subLocationFilter} onClear={() => { setSubLocationFilter(''); setRoomFilter(''); }}>
            <select value={subLocationFilter} onChange={e => { setSubLocationFilter(e.target.value); setRoomFilter(''); }} className="input-field w-auto">
              <option value="">All Sub-Locations</option>
              {subLocationOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FilterBar.Item>
        )}
        {view === 'devices' && roomOptions.length > 0 && (
          <FilterBar.Item value={roomFilter} onClear={() => setRoomFilter('')}>
            <select value={roomFilter} onChange={e => setRoomFilter(e.target.value)} className="input-field w-auto">
              <option value="">All Rooms</option>
              {roomOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </FilterBar.Item>
        )}
        {view === 'devices' && (
          <FilterBar.Item value={oemFilter} onClear={() => setOemFilter('')}>
            <select value={oemFilter} onChange={e => setOemFilter(e.target.value)} className="input-field w-auto">
              <option value="">All OEMs</option>
              {oems.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </FilterBar.Item>
        )}
      </FilterBar>

      {/* View content */}
      {view === 'locations' ? (
        <LocationsView locationFilter={locationFilter} isAdmin={isAdmin} canEditSpoc={canEditSpoc} />
      ) : (
        <DevicesView
          allDevices={allDevices}
          loading={devicesLoading}
          subLocationFilter={subLocationFilter}
          roomFilter={roomFilter}
        />
      )}
    </div>
  );
}
